/* eslint-disable sonarjs/cognitive-complexity */
import classnames from 'classnames';
import { format, isSameDay, parseISO } from 'date-fns';
import { useEffect, useMemo, useState } from 'react';
import { DayClickEventHandler, DayPicker, useActiveModifiers } from 'react-day-picker';
import {
  ProviderAvailabilityView,
  type ProviderBookingNested,
  StatusByProviderEnum,
  StatusByRepEnum,
} from '../../../../api';
import { parseTime } from '../../../../utils/time';
import { DATE_FORMAT } from '../../../constants/time';
import { useWindowSize } from '../../../hooks';
import { TooltippedText } from '../../TooltippedText';
import {
  getBookingStatusLabelText,
  getContainerClassName,
  getDateClassName,
  getFormattedLabel,
  getLabelClassName,
  getSelectedRange,
} from './Calendar.helpers';
import styles from './Calendar.module.scss';
import './Calendar.scss';

interface Props {
  isBooking: boolean;
  isLoading: boolean;
  readOnly?: boolean;
  isProvider: boolean;
  schedules: ProviderAvailabilityView[];
  selectedDates: Date[];
  onChangeSelectedDates: (selectedDates: Date[]) => void;
  onChangeMonth: (date: Date) => void;
  disabled?: Date[];
  onClickPending: (bookingRequestId: string) => void;
  currentMonth: Date;
}

export const Calendar = ({
  isBooking,
  isLoading,
  readOnly,
  schedules,
  selectedDates,
  onChangeSelectedDates,
  onChangeMonth,
  disabled,
  isProvider,
  onClickPending,
  currentMonth,
}: Props) => {
  const windowSize = useWindowSize();
  const [shiftHeld, setShiftHeld] = useState(false);
  const [previousSelectedDate, setPreviousSelectedDate] = useState<Date | null>(null);

  function downHandler({ key }) {
    if (key === 'Shift') {
      setShiftHeld(true);
    }
  }

  function upHandler({ key }) {
    if (key === 'Shift') {
      setShiftHeld(false);
    }
  }

  useEffect(() => {
    window.addEventListener('keydown', downHandler);
    window.addEventListener('keyup', upHandler);
    return () => {
      window.removeEventListener('keydown', downHandler);
      window.removeEventListener('keyup', upHandler);
    };
  }, []);

  const { bookedDates, availableDates, pendingDates, scheduleDates, selectable } = useMemo(() => {
    return schedules.reduce(
      (acc, schedule) => {
        const parsedDate = parseISO(schedule.start_on);
        acc.scheduleDates.add(parsedDate);
        if (!schedule.is_available) {
          if (!schedule.bookings?.length) {
            acc.bookedDates.add(parsedDate);
            return acc;
          }
          if (schedule.bookings?.length > 0) {
            const { status_by_provider, status_by_rep } = schedule.bookings[0];
            if (
              status_by_provider === StatusByProviderEnum.PENDING ||
              status_by_rep === StatusByRepEnum.PENDING
            ) {
              acc.pendingDates.add(parsedDate);
            }
            if (
              status_by_provider === StatusByProviderEnum.ACCEPTED &&
              status_by_rep === StatusByRepEnum.ACCEPTED
            ) {
              acc.bookedDates.add(parsedDate);
            }
            return acc;
          }
        }
        acc.availableDates.add(parsedDate);
        acc.selectable.add(schedule.start_on);
        return acc;
      },
      {
        availableDates: new Set(),
        bookedDates: new Set(),
        pendingDates: new Set(),
        scheduleDates: new Set(),
        selectable: new Set(),
      } as {
        availableDates: Set<Date>;
        bookedDates: Set<Date>;
        pendingDates: Set<Date>;
        scheduleDates: Set<Date>;
        selectable: Set<string>;
      }
    );
  }, [schedules]);

  const schedulesByDate = useMemo(() => {
    return schedules.reduce(
      (acc, schedule) => ({
        ...acc,
        [schedule.start_on]: schedule,
      }),
      {}
    );
  }, [schedules]);

  const filterUniqueDates = (dates: Date[]) => {
    return Object.values(
      dates.reduce(
        (acc: { [key: string]: Date }, date: Date): { [key: string]: Date } => ({
          ...acc,
          [format(date, DATE_FORMAT)]: date,
        }),
        {}
      )
    );
  };

  const handleDayClick: DayClickEventHandler = (date, modifier) => {
    if (isLoading) {
      return;
    }
    if (modifier.pending || modifier.booked) {
      const bookingRequestId =
        schedulesByDate?.[format(date, DATE_FORMAT)]?.bookings?.[0]?.provider_booking_request_id;
      onClickPending(bookingRequestId);
      return;
    }
    if (readOnly) {
      return false;
    }
    if (isBooking && !modifier.available) {
      return false;
    }

    const datesToSelect = getSelectedRange({
      previousSelectedDate,
      date,
      isBooking,
      shiftHeld,
      selectable,
      pendingDates,
      availableDates,
    });

    if (datesToSelect.length === 0) {
      return;
    }

    if (datesToSelect.length === 1 && modifier.selected) {
      onChangeSelectedDates(selectedDates.filter((selectedDate) => !isSameDay(selectedDate, date)));
      return;
    } else if (datesToSelect.length === 1 && !modifier.selected) {
      setPreviousSelectedDate(date);
    } else {
      setPreviousSelectedDate(null);
    }

    onChangeSelectedDates(
      filterUniqueDates([...selectedDates, ...datesToSelect]).sort(
        (a, b) => a.getTime() - b.getTime()
      )
    );
  };

  return (
    <DayPicker
      disabled={disabled}
      className="calendar"
      fixedWeeks
      showOutsideDays
      pagedNavigation
      mode="multiple"
      month={currentMonth}
      numberOfMonths={windowSize.width > 1280 ? 2 : 1}
      weekStartsOn={0}
      selected={selectedDates}
      modifiers={{
        available: Array.from(availableDates),
        booked: Array.from(bookedDates),
        pending: Array.from(pendingDates),
        schedule: Array.from(scheduleDates),
      }}
      formatters={{
        formatWeekdayName: (date) => format(date, 'EEE'),
        formatDay: (date) => (
          <CustomDay
            date={date}
            isLoading={isLoading}
            isProvider={isProvider}
            schedule={schedulesByDate?.[format(date, DATE_FORMAT)]}
          />
        ),
      }}
      onDayClick={handleDayClick}
      onMonthChange={onChangeMonth}
    />
  );
};

interface CustomDayProps {
  date: Date;
  isLoading: boolean;
  isProvider: boolean;
  schedule?: ProviderAvailabilityView;
}

const CustomDay = ({ date, isLoading, schedule, isProvider }: CustomDayProps) => {
  //
  // Priority: loading > booked > selected > available or unavailable
  //
  // If the user is booking (isBooking) and the date is not available, then return an unavailable date.
  //

  const activeModifiers = useActiveModifiers(date);

  const getLabel = () => {
    if (isLoading) {
      return 'Loading';
    }
    if (activeModifiers.booked) {
      return 'Booked';
    }
    if (schedule) {
      return getFormattedLabel(schedule.shift_type, schedule, isProvider);
    }
    return <>&nbsp;</>;
  };

  const renderDay = () => (
    <div
      className={classnames(
        styles.container,
        styles[
          getContainerClassName({
            activeModifiers,
            isProvider,
            isLoading,
            schedule,
          })
        ]
      )}
    >
      <div
        className={classnames(styles.date, styles[getDateClassName(activeModifiers, isLoading)])}
      >
        {date.getDate()}
      </div>
      <div
        className={classnames(
          styles.label,
          styles[getLabelClassName(activeModifiers, isLoading, isProvider)]
        )}
      >
        {getLabel()}
      </div>
    </div>
  );

  const renderTooltip = (booking: ProviderBookingNested) => {
    const start = format(parseTime(booking.start_time), 'HH:mm');
    const end = format(parseTime(booking.end_time), 'HH:mm');
    return (
      <ul className="list-unstyled tooltiped-info-block">
        <li className="tooltiped-info-item">{`Status: ${getBookingStatusLabelText(booking)}`}</li>
        <li className="tooltiped-info-item">{`Booking time: ${start}-${end}`}</li>
        <li className="tooltiped-info-item">{`Facility: ${booking.facility_name}`}</li>
      </ul>
    );
  };

  return schedule && schedule.bookings?.length ? (
    <TooltippedText
      tooltipContent={renderTooltip(schedule.bookings[0])}
      spanClassName="tooltiped-info"
    >
      {renderDay()}
    </TooltippedText>
  ) : (
    renderDay()
  );
};
